import { useEffect, useState } from 'react';
import { fetchProgram, type ProgramSnapshot } from '../../lib/programApi.js';
import { createSessionCache } from '../../lib/sessionCache.js';

export interface UseProgram {
  snapshot: ProgramSnapshot | null;
  loading: boolean;
  error: string | null;
}

// Caché de sesión (stale-while-revalidate): una revisita (Home/Programa) monta
// con el último snapshot al instante; el fetch de fondo revalida. Se limpia al
// cambiar de usuario (AuthProvider → clearSessionCaches).
const snapshotCache = createSessionCache<ProgramSnapshot>();

/** Carga el snapshot del programa al montar (con caché de sesión). */
export function useProgram(): UseProgram {
  const cached = snapshotCache.get();
  const [snapshot, setSnapshot] = useState<ProgramSnapshot | null>(cached);
  const [loading, setLoading] = useState(cached === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    fetchProgram(ac.signal)
      .then((s) => {
        // Cachear aunque el componente ya se haya desmontado: el dato es
        // igual de fresco y le sirve al próximo mount.
        snapshotCache.set(s);
        if (ac.signal.aborted) return;
        setSnapshot(s);
        setError(null);
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return;
        setError(e instanceof Error ? e.message : 'No pudimos cargar el programa.');
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, []);

  return { snapshot, loading, error };
}
